// client/src/services/api.js
// Centralized API service for communicating with the Node.js backend

import axios from "axios";
import { API_BASE_URL, SERVER_ROOT } from "../config/apiBase";

const BASE_URL = API_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 
    "Content-Type": "application/json",
    "x-api-key": process.env.REACT_APP_API_KEY || "your_secure_api_key_here"
  },
});

// Request interceptor — attach JWT token if present
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("shipguard_token");
    if (token && token !== "dev") {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - normalize errors + handle auth expiry
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status || 0;
    // Auto-logout on 401 (expired/invalid token)
    if (status === 401 && localStorage.getItem("shipguard_token")) {
      localStorage.removeItem("shipguard_token");
      localStorage.removeItem("shipguard_user");
      window.location.reload();
    }
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An unexpected error occurred";
    const err = new Error(message);
    err.status = status;
    err.data = error.response?.data;
    return Promise.reject(err);
  }
);

// ─────────────────────────────────────────────
// Health & Connection
// ─────────────────────────────────────────────
export const checkHealth = () => apiClient.get("/health");
export const checkShopifyConnection = () => apiClient.get("/health/shopify");
export const checkShopifyScopes = () => apiClient.get("/health/shopify/scopes");

// ─────────────────────────────────────────────
// Orders
// ─────────────────────────────────────────────

/**
 * Fetch paginated orders
 * @param {Object} params - Query params: status, limit, financial_status, fulfillment_status, page_info
 */
export const fetchOrders = (params = {}) => apiClient.get("/orders", { params });

/**
 * Get total order count
 * @param {Object} params - Filter params
 */
export const fetchOrderCount = (params = {}) => apiClient.get("/orders/count", { params });

/**
 * Get aggregated order statistics
 */
export const fetchOrderStats = (params = {}) => apiClient.get("/orders/stats", { params });

/**
 * Distinct order-level fulfillment statuses (with counts) for the current
 * date/location window — feeds the status dropdown next to the Location filter
 */
export const fetchOrderStatuses = (params = {}) => apiClient.get("/orders/statuses", { params });

/**
 * Fetch a single order by ID (ShipHero ID or order number)
 * @param {string} id - ShipHero order ID or order number
 */
export const fetchOrderById = (id) => apiClient.get(`/orders/${id}`);
export const fetchOrderHistory = (id) => apiClient.get(`/orders/${id}/history`);

/** Live Shopify lookup of an order's per-line-item assigned locations */
export const verifyOrderLocation = (orderNumber) =>
  apiClient.get("/orders/verify-location", { params: { order: orderNumber } });

/** Trigger ShipHero order import (runs in background) */
export const triggerOrderImport = (reset = false) =>
  apiClient.post("/orders/import", { reset, autoValidate: true });

/** Trigger Shopify validation on unvalidated orders (runs in background) */
export const triggerOrderValidation = () =>
  apiClient.post("/orders/validate");

/** Trigger 30-day full sync (all statuses) + validation */
export const triggerSync30d = () => apiClient.post("/orders/sync-30d");

/** Reset all validations and re-validate all orders with fresh ShipHero line items */
export const triggerRevalidateAll = () => apiClient.post("/orders/revalidate-all");

/** Re-derive per-line-item statuses (Shopify item changed / location disabled)
 *  for all existing DB orders. Cheap/local — no ShipHero or Shopify API calls. */
export const triggerRevalidateLineStatuses = () =>
  apiClient.post("/orders/revalidate-line-statuses");

/** Poll progress of the line-item revalidation job */
export const fetchRevalidateLineStatusesStatus = () =>
  apiClient.get("/orders/revalidate-line-statuses/status");

/** Poll progress of the 30-day sync job */
export const fetchSync30dStatus = () => apiClient.get("/orders/sync-30d/status");

/** Poll progress of the Sync from ShipHero job */
export const fetchImportStatus = () => apiClient.get("/orders/import/status");

/** Poll progress of the Validate with Shopify / Re-validate All job */
export const fetchValidateStatus = () => apiClient.get("/orders/validate/status");

/** Import specific orders by order numbers */
export const importOrdersByNumbers = (orderNumbers = [], autoValidate = true) =>
  apiClient.post("/orders/import-order-numbers", { orderNumbers, autoValidate }, { timeout: 120000 });

/** List retention backup CSV files */
export const fetchRetentionBackups = () => apiClient.get("/orders/retention/backups");

// ─────────────────────────────────────────────
// Audit (Layer 2 Protection)
// ─────────────────────────────────────────────

/** Trigger a full batch audit (fire-and-forget, returns immediately) */
export const triggerAudit = () => apiClient.post("/audit/run");

/** Get current audit run status and summary */
export const fetchAuditStatus = () => apiClient.get("/audit/status");

/** Get paginated audit results */
export const fetchAuditResults = (params = {}) => apiClient.get("/audit/results", { params });

/** Get only mismatch records */
export const fetchAuditMismatches = (params = {}) => apiClient.get("/audit/mismatches", { params });

// ─────────────────────────────────────────────
// Webhook Events
// ─────────────────────────────────────────────

// Webhook events live at /webhooks/events (not under /api/)
// Use the same resolved API base and strip trailing /api for server-root paths.
const rootClient = axios.create({
  baseURL: SERVER_ROOT,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});
rootClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.error || error.message || "Request failed";
    const err = new Error(message);
    err.status = error.response?.status || 0;
    return Promise.reject(err);
  }
);

/** Fetch recent webhook events (Shopify + ShipHero) */
export const fetchWebhookEvents = (params = {}) =>
  rootClient.get("/webhooks/events", { params });

// ─────────────────────────────────────────────
// Command Module (Batch Workflow)
// ─────────────────────────────────────────────

/** Get command config (tokens masked) */
export const fetchCommandConfig = () => apiClient.get("/command/config");

/** Save command config */
export const saveCommandConfig = (data) => apiClient.put("/command/config", data);

/** Fetch Shopify locations via command module */
export const fetchCommandLocations = () => apiClient.get("/command/shopify/locations");

/** Get paginated command orders */
export const fetchCommandOrders = (params = {}) => apiClient.get("/command/orders", { params });

/** Get command order stats */
export const fetchCommandStats = () => apiClient.get("/command/orders/stats");

/** Get auto-fetch scheduler status */
export const fetchAutoFetchStatus = () => apiClient.get("/command/auto-fetch/status");

/** Get recent command jobs */
export const fetchCommandJobs = () => apiClient.get("/command/jobs");

/** Get a single job by ID */
export const fetchCommandJob = (id) => apiClient.get(`/command/jobs/${id}`);

/** Start a fetch job */
export const startFetchJob = (resume = false, clearExisting = false) =>
  apiClient.post("/command/jobs/fetch/start", { resume, clearExisting });

/** Start a validation job */
export const startValidateJob = (reset = false) =>
  apiClient.post("/command/jobs/validate/start", { reset });

/** Start an execution job */
export const startExecuteJob = (orderIds = []) =>
  apiClient.post("/command/jobs/execute/start", { orderIds });

/** Start a history scan job */
export const startHistoryScanJob = () => apiClient.post("/command/jobs/history-scan/start");

/** Abort/pause a running job */
export const abortCommandJob = (id) => apiClient.post(`/command/jobs/${id}/abort`);

/** Get execution results */
export const fetchCommandResults = (params = {}) =>
  apiClient.get("/command/results", { params });

/** Get CSV export URL */
export const getCommandExportUrl = () => `${BASE_URL}/command/results/export`;

/** Download a CSV file via fetch (avoids CRA proxy issues with anchor navigation) */
export const downloadCsv = async (url) => {
  const headers = { "x-api-key": process.env.REACT_APP_API_KEY || "your_secure_api_key_here" };
  const token = localStorage.getItem("shipguard_token");
  if (token && token !== "dev") {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const response = await axios.get(url, {
    responseType: "blob",
    headers,
  });
  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "export.csv";
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/** Download orders CSV export */
export const downloadOrdersCsv = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return downloadCsv(`${BASE_URL}/orders/export${qs ? `?${qs}` : ""}`);
};

/** Download a retention backup CSV by filename */
export const downloadRetentionBackup = (filename) =>
  downloadCsv(`${BASE_URL}/orders/retention/backups/${encodeURIComponent(filename)}`);

/** Download command results CSV export */
export const downloadCommandCsv = () => downloadCsv(`${BASE_URL}/command/results/export`);

/** Check command DB health */
export const checkCommandDbHealth = () => apiClient.get("/command/db/health");

// ─────────────────────────────────────────────
// Watchdog Module (Event-Driven Pipeline)
// ─────────────────────────────────────────────

/** Get watchdog aggregate stats by status */
export const fetchWatchdogStats = () => apiClient.get("/watchdog/stats");

/** Get paginated watched orders */
export const fetchWatchdogOrders = (params = {}) => apiClient.get("/watchdog/orders", { params });

/** Get single watched order detail */
export const fetchWatchdogOrder = (id) => apiClient.get(`/watchdog/orders/${id}`);

/** Get recent watchdog activity log */
export const fetchWatchdogActivity = (params = {}) => apiClient.get("/watchdog/activity", { params });

/** Get watchdog config */
export const fetchWatchdogConfig = () => apiClient.get("/watchdog/config");

/** Save watchdog config */
export const saveWatchdogConfig = (data) => apiClient.put("/watchdog/config", data);

/** Manually execute action on a watched order */
export const executeWatchdogOrder = (id) => apiClient.post(`/watchdog/orders/${id}/execute`);

/** Dismiss a watched order */
export const dismissWatchdogOrder = (id) => apiClient.post(`/watchdog/orders/${id}/dismiss`);

/** Force re-validation of a watched order */
export const revalidateWatchdogOrder = (id) => apiClient.post(`/watchdog/orders/${id}/revalidate`);

/** Clear all completed/terminal watchdog orders */
export const clearCompletedWatchdogOrders = () => apiClient.post("/watchdog/orders/clear-completed");

/** Bulk dismiss multiple watchdog orders */
export const bulkDismissWatchdogOrders = (ids) => apiClient.post("/watchdog/orders/bulk-dismiss", { ids });

export default apiClient;

